import { staticOptions } from "lib/core/apiClient";
import { AUTHENTICATE_ME_PATH, fetchAuthenticateMe } from "lib/utils/authenticateMe";
import { AddressCardFields } from "lib/types/address";
import { UserLoginType, UserType } from "lib/types/user";
import { getAuthCookies } from "lib/utils/auth";
import { ChannelName, CheckoutMessageTypes, createBroadcastChannel } from "lib/utils/broadcastChannel";
import { eventTypes, FLO_USER_ID } from "lib/utils/constants";
import { publishPostMessage } from "lib/utils/helpers";
import { parseCustomerAttributes, parseUserAddress } from "lib/utils/user";
import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef } from "react";
import { Value as PhoneInputValueType } from "react-phone-number-input";
import useSWR, { mutate } from "swr";

const Context = createContext<UserContext>({ state: {}, actions: {} } as UserContext);

export enum ActionType {
  SET_USER,
  SET_USER_LOGIN_TYPE,
  SET_MARKETING_CONSENT,
  UPDATE_USER_ADDRESS,
  RESET_ADDRESS_LOADING,
  SET_ACCOUNT_RESPONSE_LOADING,
}

export type Action =
  | {
      type: ActionType.SET_USER;
      payload: {
        userData?: UserType;
      };
    }
  | { type: ActionType.SET_USER_LOGIN_TYPE; payload: UserLoginType }
  | { type: ActionType.SET_MARKETING_CONSENT; payload: boolean }
  | {
      type: ActionType.UPDATE_USER_ADDRESS;
      payload: {
        addresses: AddressCardFields[];
        default_billing_address: AddressCardFields;
        default_shipping_address: AddressCardFields;
        isAddressLoading?: boolean;
      };
    }
  | {
      type: ActionType.RESET_ADDRESS_LOADING;
    }
  | {
      type: ActionType.SET_ACCOUNT_RESPONSE_LOADING;
      payload: boolean;
    };

export interface UserState {
  user: UserType;
  userLoginType: UserLoginType;
  isAddressLoading: boolean;
  isAccountResponseLoading: boolean;
}

export interface UserContext {
  state: UserState;
  actions: {
    setUserData: (data: any) => void;
    setUserLoginType: (type: UserLoginType) => void;
    setMarketingConsent: (value: boolean) => void;
    setUserAddress: (
      checkoutAddresses: {
        shipping_address_id?: string;
        billing_address_id?: string;
      },
      addressResponse: Record<string, any>[],
      isAddressLoading?: boolean,
      addressConfig?: any[],
    ) => void;
    deleteAddressCache: () => void;
    updateUser: () => void;
  };
}

function reducer(state: UserState, action: Action): UserState {
  switch (action.type) {
    case ActionType.SET_USER: {
      return {
        ...state,
        user: { ...state.user, ...action.payload.userData },
        isAccountResponseLoading: false,
      };
    }
    case ActionType.SET_USER_LOGIN_TYPE: {
      return {
        ...state,
        userLoginType: action.payload,
      };
    }
    case ActionType.SET_MARKETING_CONSENT: {
      return {
        ...state,
        user: { ...state.user, marketing_consent: action.payload },
      };
    }
    case ActionType.UPDATE_USER_ADDRESS: {
      return {
        ...state,
        user: { ...state.user, ...action.payload },
        isAddressLoading: action.payload.isAddressLoading ?? UserInitialState.isAddressLoading,
      };
    }
    case ActionType.RESET_ADDRESS_LOADING: {
      return {
        ...state,
        isAddressLoading: true,
      };
    }
    case ActionType.SET_ACCOUNT_RESPONSE_LOADING: {
      return {
        ...state,
        isAccountResponseLoading: action.payload,
      };
    }
  }
  return state;
}

const UserInitialState: UserState = {
  user: {
    uid: "",
    email: "",
    phone: "" as PhoneInputValueType,
    name: "",
    hasHomeAddr: false,
    hasWorkAddr: false,
  },
  userLoginType: "na",
  isAddressLoading: true,
  isAccountResponseLoading: true,
};

export const UserProvider: React.FC<React.PropsWithChildren<{ initialState?: UserState }>> = ({
  initialState = UserInitialState,
  children,
}) => {
  const [reducerState, dispatch] = useReducer(reducer, initialState);

  const { data: accountResponse, isValidating } = useSWR(AUTHENTICATE_ME_PATH, fetchAuthenticateMe, staticOptions);

  // Dispatch event when account response loading starts
  useEffect(() => {
    if (isValidating) {
      dispatch({
        type: ActionType.SET_ACCOUNT_RESPONSE_LOADING,
        payload: true,
      });
    }
  }, [isValidating]);

  const tryMutateWithCookieCheck = () => {
    const cookies = getAuthCookies();
    // If cookies are available, execute the mutate calls and stop retrying
    if (cookies) {
      mutate("/gatekeeper/accounts?include=address");
      mutate(AUTHENTICATE_ME_PATH);

      return true; // Success
    }

    return false; // Cookies not available yet
  };

  useEffect(() => {
    const channel = createBroadcastChannel(ChannelName.SHOPFLO_CHANNEL);
    channel?.onMessage((message) => {
      if (message.type === CheckoutMessageTypes.USER_LOGGED_IN) {
        tryMutateWithCookieCheck();
      }
    });
    return () => {
      channel?.close();
    };
  }, []);

  // setting user basic data from /authenticate/me
  useEffect(() => {
    if (!accountResponse?.id && !isValidating) {
      dispatch({
        type: ActionType.SET_ACCOUNT_RESPONSE_LOADING,
        payload: false,
      });
      return;
    }

    if (!accountResponse?.id) {
      return;
    }

    const userData = parseCustomerAttributes(accountResponse);
    sessionStorage.setItem(FLO_USER_ID, userData.uid);

    dispatch({
      type: ActionType.SET_USER,
      payload: {
        userData,
      },
    });
  }, [accountResponse?.id, isValidating]);

  // setting user order history when we have all required data
  const setUserData = useCallback((data: any) => {
    dispatch({
      type: ActionType.SET_USER,
      payload: {
        userData: data,
      },
    });
  }, []);

  const setUserLoginType = useCallback((type: UserLoginType) => {
    dispatch({
      type: ActionType.SET_USER_LOGIN_TYPE,
      payload: type,
    });
  }, []);

  const setMarketingConsent = useCallback((value: boolean) => {
    dispatch({
      type: ActionType.SET_MARKETING_CONSENT,
      payload: value,
    });
  }, []);

  const updateUser = useCallback(() => {
    mutate(AUTHENTICATE_ME_PATH);
    mutate("/gatekeeper/accounts?include=address");
    const channel = createBroadcastChannel(ChannelName.SHOPFLO_CHANNEL);
    channel.broadcast(CheckoutMessageTypes.USER_LOGGED_IN, {});
  }, []);

  const setUserAddress = useCallback(
    (
      checkoutAddresses: {
        shipping_address_id?: string;
        billing_address_id?: string;
      },
      addressResponse: Record<string, any>[],
      isAddressLoading?: boolean,
      addressConfig?: any[],
    ) => {
      const { addresses, default_billing_address, default_shipping_address } = parseUserAddress(
        checkoutAddresses,
        addressResponse,
        addressConfig,
      );
      dispatch({
        type: ActionType.UPDATE_USER_ADDRESS,
        payload: {
          addresses,
          default_billing_address,
          default_shipping_address,
          isAddressLoading,
        },
      });
    },
    [],
  );

  const deleteAddressCache = useCallback(() => {
    dispatch({
      type: ActionType.RESET_ADDRESS_LOADING,
    });
    mutate(
      () => ["/gatekeeper/accounts?include=address", AUTHENTICATE_ME_PATH],
      undefined,
      false,
    );
  }, []);

  const actions = useMemo(
    () => ({
      setUserData,
      setUserLoginType,
      setMarketingConsent,
      setUserAddress,
      deleteAddressCache,
      updateUser,
    }),
    [setUserData, setUserLoginType, setMarketingConsent, setUserAddress, deleteAddressCache, updateUser],
  );

  const prevUser = useRef<UserType>(reducerState.user);

  useEffect(() => {
    if (reducerState.user.uid && reducerState.user.uid !== prevUser.current.uid) {
      publishPostMessage(eventTypes.FLO_GHOST_USER, { userId: reducerState.user.uid });
    }
    prevUser.current = reducerState.user;
  }, [reducerState.user]);

  return (
    <Context.Provider
      value={{
        state: {
          ...reducerState,
        },
        actions,
      }}>
      {children}
    </Context.Provider>
  );
};

export function useUserContext() {
  if (!Boolean(Context)) throw new Error("useUserContext must be used within a UserProvider");
  const UserContext = useContext(Context);
  return UserContext as UserContext;
}
